import { Component, OnInit, inject } from "@angular/core";
import { FormatType } from "@enums/FormatType";
import { ModuleType } from "@enums/ModuleType";
import { RendererType } from "@enums/RendererType";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { WorkspaceButtonConfig } from "@models/workspace/WorkspaceButtonConfig";
import { DocumentDataService } from "@services/http/document/DocumentDataService";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { ToastrService } from "ngx-toastr";
import { firstValueFrom } from "rxjs";
import { nameof } from "@methods/CommonMethods";
import { DocumentBulkDeletedModel } from "@app/audit/shared/data-models/document-bulk-deleted-model";
import { WorkspaceComponent } from "@app/shared/workspace/workspace.component";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "../i18n/scope";

@Component({
    selector: "bulk-deleted-documents",
    templateUrl: "./bulk-deleted-documents.component.html",
    styleUrls: ["./bulk-deleted-documents.component.scss"],
    imports: [WorkspaceComponent, TranslatePipe],
    viewProviders: [provideTranslationScope(scope)],
})
export class BulkDeletedDocumentsComponent implements OnInit {
    private permissionsService = inject(PermissionsService);
    private toastr = inject(ToastrService);
    private dataService = inject(DocumentDataService);
    private translation = injectTranslation(scope);

    moduleType = ModuleType.CD;
    tabID: string;
    tab: TabModel;
    data: Promise<DocumentBulkDeletedModel[]>;
    headerLookup: { [propName: string]: string } = {};
    permissions: WorkspaceModulePermissionModel;
    filterConfigs: FilterConfig[] = [];
    rendererConfig: RendererConfig[] = [
        {
            textColumn: nameof<DocumentBulkDeletedModel>("uploadedOn"),
            type: RendererType.Default,
            formatType: FormatType.DateTimeFormat,
        },
        {
            textColumn: nameof<DocumentBulkDeletedModel>("deletedOn"),
            type: RendererType.Default,
            formatType: FormatType.DateTimeFormat,
        },
    ];

    buttonConfig: WorkspaceButtonConfig[] = [];

    numberProps: string[] = [];
    dateProps: string[] = [
        nameof<DocumentBulkDeletedModel>("uploadedOn"),
        nameof<DocumentBulkDeletedModel>("deletedOn"),
    ];

    constructor() {
        this.permissions = {
            canAdd: false,
            canOpen: false,

            isModuleAdmin: false,
            canPublish: false,
            canSetSystemDefault: false,
        };
    }

    async ngOnInit(): Promise<void> {
        // The template creates cerrix-workspace only once `data` is set, so the header lookup
        // below is translated before the workspace reads it.
        await this.translation.ready();
        const hasAccess = this.permissionsService.permissions.Documents.Admin;
        if (!hasAccess) {
            this.toastr.error(this.translation.t("bulkDeleted.noAccess"));
            this.tab.close(false);
        }

        this.headerLookup = this.getHeaderLookup();

        this.data = firstValueFrom(this.dataService.GetBulkDeletedDocuments()).then(
            this.mapHeaders
        );
    }

    mapHeaders = (rows) => {
        if (rows && rows.length) {
            Object.getOwnPropertyNames(rows[0]).forEach((propName) => {
                if (!this.headerLookup[propName]) {
                    this.headerLookup[propName] = propName;
                }
            });
        }
        return rows;
    };

    private getHeaderLookup(): { [propName: string]: string } {
        const headerLookup = {};
        const t = (key: string) => this.translation.t(key);
        headerLookup[nameof<DocumentBulkDeletedModel>("guid")] = t("bulkDeleted.guidHeader");
        headerLookup[nameof<DocumentBulkDeletedModel>("name")] = t("bulkDeleted.nameHeader");
        headerLookup[nameof<DocumentBulkDeletedModel>("uploadedBy")] = t("bulkDeleted.uploadedByHeader");
        headerLookup[nameof<DocumentBulkDeletedModel>("uploadedOn")] = t("bulkDeleted.uploadedOnHeader");
        headerLookup[nameof<DocumentBulkDeletedModel>("sourceTypeName")] = t("bulkDeleted.sourceHeader");
        headerLookup[nameof<DocumentBulkDeletedModel>("typeName")] = t("bulkDeleted.typeHeader");
        headerLookup[nameof<DocumentBulkDeletedModel>("deletedBy")] = t("bulkDeleted.deletedByHeader");
        headerLookup[nameof<DocumentBulkDeletedModel>("deletedOn")] = t("bulkDeleted.deletedOnHeader");

        return headerLookup;
    }
}
